import * as React from 'react';
import type { AssistantInputField } from '@cdf/api-client-react';

import { TimeField } from '@/components/booking/time-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240, 480] as const;

export function AssistantInputForm({
  fields,
  disabled,
  onSend
}: {
  fields: AssistantInputField[];
  disabled: boolean;
  onSend: (message: string) => Promise<void>;
}) {
  const [values, setValues] = React.useState<Record<string, string>>({});
  const valueOf = (id: string) => Object.hasOwn(values, id) && typeof values[id] === 'string' ? values[id] : '';
  const filled = fields.filter((field) => valueOf(field.id).trim());
  const setValue = (id: string, value: string) => setValues((current) => ({ ...current, [id]: value }));

  return (
    <form
      className="mr-auto w-full max-w-md space-y-3 rounded-lg border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!filled.length || disabled) return;
        const details = filled.map((field) => {
          const value = valueOf(field.id).trim();
          return `${field.label}: ${field.kind === 'duration' ? `${value} minutes` : value}`;
        });
        void onSend(`For my request: ${details.join('; ')}.`);
      }}
      aria-label="Optional details for the assistant"
    >
      <p className="text-xs text-muted-foreground">Add any details you know, or reply in the chat below.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((field) => (
          <div key={field.id} className="space-y-1">
            <label htmlFor={`assistant-field-${field.id}`} className="text-xs font-medium">{field.label}</label>
            {field.kind === 'time' ? (
              <TimeField
                id={`assistant-field-${field.id}`}
                value={valueOf(field.id)}
                onValueChange={(value) => setValue(field.id, value)}
                disabled={disabled}
                hourAriaLabel={`${field.label} hour`}
                minuteAriaLabel={`${field.label} minute`}
              />
            ) : field.kind === 'duration' ? (
              <Select value={valueOf(field.id)} onValueChange={(value) => setValue(field.id, value)} disabled={disabled}>
                <SelectTrigger id={`assistant-field-${field.id}`} className="w-full"><SelectValue placeholder="Choose duration" /></SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((minutes) => (
                    <SelectItem key={minutes} value={String(minutes)}>{minutes < 60 ? `${minutes} minutes` : `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`assistant-field-${field.id}`}
                type={field.kind === 'date' ? 'date' : 'text'}
                value={valueOf(field.id)}
                onChange={(event) => setValue(field.id, event.target.value)}
                maxLength={field.kind === 'text' ? 120 : undefined}
                disabled={disabled}
              />
            )}
            {(field.kind === 'time' || field.kind === 'duration') && valueOf(field.id) ? (
              <Button type="button" size="sm" variant="ghost" className="h-6 px-1 text-xs" disabled={disabled} onClick={() => setValue(field.id, '')}>
                Clear {field.label.toLowerCase()}
              </Button>
            ) : null}
          </div>
        ))}
      </div>
      <Button type="submit" size="sm" disabled={disabled || !filled.length}>Send details</Button>
    </form>
  );
}
